import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchCollabPendingAwareness,
    acknowledgeAwareness,
} from "../../vsecurite/vsecuriteSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs, { Localize } from "../../../app/lang/langs";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Fade from "@mui/material/Fade";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

/* ── Brand / palette constants ── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const ORANGE_GRADIENT = "linear-gradient(135deg, #FF8701, #FFC100)";
const ORANGE_GLOW = "rgba(255,144,21,0.25)";
const GREEN_GRADIENT = "linear-gradient(135deg, #4ade80, #22c55e)";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const BG_GRADIENT = "linear-gradient(160deg, #f7f8fa 0%, #eef0f4 50%, #e8eaef 100%)";
const SURFACE = "rgba(255,255,255,0.90)";
const GREEN = "#16a34a";
const EASE = "cubic-bezier(0.4,0,0.2,1)";

/* ── CSS keyframe injection ── */
const KEYFRAMES = `
    @keyframes awarenessCollabFadeInUp {
        from { opacity: 0; transform: translateY(14px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`;

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function AwarenessCollab() {
    const dispatch = useDispatch();

    /* ── Redux state selectors ── */
    const pendingList = useSelector((state) => state.vsecurite.awareness.collabPending);
    const loading = useSelector((state) => state.vsecurite.awareness.loading);
    const lang = useSelector((state) => state.settings.lang);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const mobile = isMobile === 2;

    /* ── Local component state ── */
    /* confirmOpen: tracks which campaign id is awaiting user confirmation, null = closed */
    const [confirmOpen, setConfirmOpen] = useState(null);
    /* submitting: id of the campaign currently being acknowledged via the API */
    const [submitting, setSubmitting] = useState(null);
    /* readOpen: tracks which campaign id's content is shown in the full-screen reader dialog, null = closed */
    const [readOpen, setReadOpen] = useState(null);

    /* hasFetched: tracks whether THIS component's own fetch has completed at least once.
       Prevents the empty-state from flashing due to the shared `awareness.loading` flag
       being toggled by other thunks (e.g. fetchCollabAwarenessHistory).
       Must be useState (not useRef) so that setting it triggers a re-render. */
    const [hasFetched, setHasFetched] = useState(false);

    /* ── Fetch pending campaigns on component mount ── */
    useEffect(() => {
        dispatch(fetchCollabPendingAwareness()).finally(() => {
            setHasFetched(true);
        });
    }, [dispatch]);

    /**
     * handleAcknowledgeClick
     * Opens the confirmation dialog for the chosen campaign.
     * @param {number|string} campaignId - The id of the campaign to acknowledge
     */
    const handleAcknowledgeClick = (campaignId) => {
        setConfirmOpen(campaignId);
    };

    /**
     * handleConfirmClose
     * Closes the confirmation dialog without taking action.
     */
    const handleConfirmClose = () => {
        setConfirmOpen(null);
    };

    /**
     * handleConfirmAcknowledge
     * Dispatches the acknowledgeAwareness thunk for the confirmed campaign.
     * On success: shows a success toast and re-fetches the pending list.
     * On failure: shows an error toast.
     * This action is IRREVERSIBLE — the employee cannot un-acknowledge a campaign.
     */
    const handleConfirmAcknowledge = async () => {
        const id = confirmOpen;
        setConfirmOpen(null);
        setSubmitting(id);
        try {
            const result = await dispatch(acknowledgeAwareness({ id_campaign: id }));
            /* Check for a rejected action (thunk rejectWithValue or thrown error) */
            if (result.error) {
                showErrorToast(Localize("VSEC_TOAST_GENERIC_ERROR", lang));
            } else {
                showSuccessToast(Localize("VSEC_TOAST_CAMPAIGN_APPROVED", lang));
                dispatch(fetchCollabPendingAwareness());
            }
        } catch {
            showErrorToast(Localize("VSEC_TOAST_GENERIC_ERROR", lang));
        } finally {
            setSubmitting(null);
        }
    };

    /* Helper: find campaign object by id for the reader dialog */
    const readCampaign = readOpen !== null
        ? (pendingList || []).find((c) => c.id === readOpen)
        : null;

    /* ── Full-page loading skeleton while initial fetch runs ── */
    /* Show spinner if Redux says loading OR if this component's own fetch hasn't resolved yet */
    if (loading || !hasFetched) {
        if (!pendingList || pendingList.length === 0) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 320,
                        background: BG_GRADIENT,
                        borderRadius: "20px",
                        mt: 0,
                    }}
                >
                    <CircularProgress sx={{ color: ORANGE }} size={40} thickness={3.5} />
                </Box>
            );
        }
    }

    /* ── Empty state — no pending campaigns ── */
    /* Only show after this component's own fetch has completed, preventing the flash
       caused by the shared loading flag being reset by other awareness thunks */
    if (hasFetched && !loading && (!pendingList || pendingList.length === 0)) {
        return (
            <>
                <style>{KEYFRAMES}</style>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        minHeight: 320,
                        background: BG_GRADIENT,
                        borderRadius: "20px",
                        p: mobile ? 3 : 4,
                        mt: 0,
                        animation: `awarenessCollabFadeInUp 500ms ${EASE} both`,
                    }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: "20px",
                            background: "rgba(22,163,74,0.08)",
                            border: "1px solid rgba(22,163,74,0.16)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                        }}
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: 36, color: GREEN }} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: "1.2rem",
                            fontWeight: 650,
                            color: TEXT_PRIMARY,
                            letterSpacing: "-0.02em",
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                            textAlign: "center",
                        }}
                    >
                        <Langs str="VSEC_AWARENESS_UP_TO_DATE" />
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "0.9rem",
                            color: TEXT_MUTED,
                            fontWeight: 400,
                            fontFamily: "'Inter',sans-serif",
                            textAlign: "center",
                        }}
                    >
                        <Langs str="VSEC_AWARENESS_EMPTY_PENDING" />
                    </Typography>
                </Box>
            </>
        );
    }

    /* ── Main list of pending campaigns ── */
    return (
        <>
            <style>{KEYFRAMES}</style>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: mobile ? 2.5 : 3,
                    p: { xs: 2, sm: 3 },
                    background: BG_GRADIENT,
                    borderRadius: "20px",
                    mt: 0,
                    height: "fit-content",
                }}
            >
                {(pendingList || []).map((campaign, idx) => (
                    <Fade in key={campaign.id} timeout={400 + idx * 80}>
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: mobile ? "14px" : "18px",
                                background: SURFACE,
                                border: `1px solid ${BORDER_SUBTLE}`,
                                overflow: "hidden",
                                position: "relative",
                                minHeight: mobile ? "auto" : 400,
                                transition: `all 350ms ${EASE}`,
                                animation: `awarenessCollabFadeInUp 500ms ${EASE} ${idx * 90}ms both`,
                                "&:hover": {
                                    border: `1px solid rgba(255,144,21,0.20)`,
                                    boxShadow: `0 4px 24px rgba(255,144,21,0.07), 0 1px 6px rgba(0,0,0,0.04)`,
                                    transform: mobile ? "none" : "translateY(-1px)",
                                },
                                /* Orange accent bar at the top of each card */
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: "3px",
                                    background: `linear-gradient(90deg, transparent 5%, ${ORANGE_GLOW} 50%, transparent 95%)`,
                                },
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: { xs: mobile ? 3 : 2, sm: 3 },
                                    "&:last-child": { pb: mobile ? 3 : 2 },
                                }}
                            >

                                {/* ── Campaign title ── */}
                                <Stack direction="row" alignItems="center" spacing={mobile ? 2 : 1.5} sx={{ mb: mobile ? 2 : 1 }}>
                                    <Box
                                        sx={{
                                            width: mobile ? 40 : 34,
                                            height: mobile ? 40 : 34,
                                            borderRadius: "10px",
                                            background: ORANGE_LIGHT,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <TaskAltRoundedIcon sx={{ color: ORANGE, fontSize: mobile ? 22 : 18 }} />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 650,
                                            fontSize: mobile ? "1.05rem" : { xs: "0.95rem", sm: "1.1rem" },
                                            color: TEXT_PRIMARY,
                                            letterSpacing: "-0.02em",
                                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {campaign.title}
                                    </Typography>
                                </Stack>

                                {/* ── Campaign description ── */}
                                {campaign.description && (
                                    <Typography
                                        component="div"
                                        sx={{
                                            fontSize: "0.9rem",
                                            color: TEXT_SECONDARY,
                                            fontWeight: 400,
                                            fontFamily: "'Inter',sans-serif",
                                            mb: mobile ? 2.5 : 2,
                                            lineHeight: 1.6,
                                            whiteSpace: "pre-wrap",
                                        }}
                                        dangerouslySetInnerHTML={{ __html: campaign.description }}
                                    />
                                )}

                                {/* ── Divider between description and document content ── */}
                                <Divider
                                    sx={{
                                        my: mobile ? 2.5 : 2,
                                        borderColor: BORDER_SUBTLE,
                                    }}
                                />

                                {/* ── Document content area ── */}
                                {campaign.content && (
                                    mobile ? (
                                        /* Mobile: "Lire le contenu" button that opens full-screen reader */
                                        <Box sx={{ mb: 2.5 }}>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                startIcon={<OpenInNewRoundedIcon />}
                                                onClick={() => setReadOpen(campaign.id)}
                                                sx={{
                                                    borderRadius: "12px",
                                                    minHeight: 48,
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    fontSize: "0.9rem",
                                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                                    borderColor: ORANGE,
                                                    color: ORANGE,
                                                    transition: `all 250ms ${EASE}`,
                                                    "&:hover": {
                                                        borderColor: ORANGE,
                                                        background: ORANGE_LIGHT,
                                                    },
                                                    "&:active": { transform: "scale(0.98)" },
                                                }}
                                            >
                                                Lire le contenu
                                            </Button>
                                        </Box>
                                    ) : (
                                        /* Desktop: inline content box.
                                           No nested scroll — the outer list is the
                                           single scroll surface, so long content
                                           never traps wheel events away from the
                                           approve button below. */
                                        <Box
                                            sx={{
                                                overflowX: "hidden",
                                                mb: 2.5,
                                                p: 2,
                                                borderRadius: "12px",
                                                background: "rgba(0,0,0,0.025)",
                                                border: `1px solid ${BORDER_SUBTLE}`,
                                            }}
                                        >
                                            <Typography
                                                component="div"
                                                sx={{
                                                    fontSize: "0.88rem",
                                                    color: TEXT_SECONDARY,
                                                    fontFamily: "'Inter',sans-serif",
                                                    lineHeight: 1.7,
                                                    wordBreak: "break-word",
                                                    whiteSpace: "pre-wrap",
                                                }}
                                                dangerouslySetInnerHTML={{ __html: campaign.content }}
                                            />
                                        </Box>
                                    )
                                )}

                                {/* ── Divider before action area ── */}
                                <Divider sx={{ mb: mobile ? 2.5 : 2.5, borderColor: BORDER_SUBTLE }} />

                                {/* ── Irreversibility warning note ── */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    sx={{
                                        mb: mobile ? 2.5 : 2,
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: "10px",
                                        background: "rgba(255,144,21,0.05)",
                                        border: "1px solid rgba(255,144,21,0.14)",
                                    }}
                                >
                                    <WarningAmberRoundedIcon
                                        sx={{ color: ORANGE, fontSize: 16, flexShrink: 0 }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: "0.75rem",
                                            color: TEXT_MUTED,
                                            fontFamily: "'Inter',sans-serif",
                                            fontWeight: 500,
                                        }}
                                    >
                                        <Langs str="VSEC_IRREVERSIBLE_WARNING" />
                                    </Typography>
                                </Stack>

                                {/* ── Acknowledge action button ── */}
                                <Box sx={{ display: "flex", justifyContent: mobile ? "stretch" : "flex-end", mt: 1 }}>
                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: mobile ? "block" : "inline-block",
                                            width: mobile ? "100%" : "auto",
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            disabled={submitting === campaign.id}
                                            onClick={() => handleAcknowledgeClick(campaign.id)}
                                            fullWidth={mobile}
                                            startIcon={
                                                submitting === campaign.id ? (
                                                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                                                ) : (
                                                    <CheckCircleOutlineIcon sx={{ fontSize: "18px !important" }} />
                                                )
                                            }
                                            sx={{
                                                borderRadius: "12px",
                                                px: 3,
                                                py: 1,
                                                minHeight: 48,
                                                textTransform: "none",
                                                fontWeight: 650,
                                                fontSize: "0.9rem",
                                                letterSpacing: "-0.01em",
                                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                                background: GREEN_GRADIENT,
                                                color: "#fff",
                                                boxShadow: `0 4px 14px rgba(74,222,128,0.30), 0 1px 4px rgba(74,222,128,0.20)`,
                                                transition: `all 280ms ${EASE}`,
                                                "&:hover": {
                                                    background: GREEN_GRADIENT,
                                                    boxShadow: `0 6px 20px rgba(74,222,128,0.40), 0 2px 8px rgba(74,222,128,0.25)`,
                                                    transform: "translateY(-1px)",
                                                },
                                                "&:active": { transform: "scale(0.98)" },
                                                "&.Mui-disabled": {
                                                    background: "rgba(0,0,0,0.06)",
                                                    color: "rgba(0,0,0,0.26)",
                                                    boxShadow: "none",
                                                },
                                            }}
                                        >
                                            {submitting === campaign.id ? <Langs str="VSEC_BTN_IN_PROGRESS" /> : <Langs str="VSEC_BTN_READ_APPROVED" />}
                                        </Button>

                                        {/* Spark — orbits the button's rounded perimeter while idle */}
                                        {submitting !== campaign.id && (
                                            <Box
                                                aria-hidden="true"
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: "50%",
                                                    background:
                                                        "radial-gradient(circle, #ffffff 0%, #bbf7d0 45%, rgba(74,222,128,0.55) 75%, transparent 90%)",
                                                    boxShadow:
                                                        "0 0 10px 2px rgba(255,255,255,0.9), 0 0 18px 6px rgba(74,222,128,0.65), 0 0 32px 10px rgba(74,222,128,0.35)",
                                                    /* Trace the button's rounded outline */
                                                    offsetPath: "rect(0% 100% 100% 0% round 12px)",
                                                    offsetDistance: "0%",
                                                    /* Center the spark on the path */
                                                    offsetAnchor: "center",
                                                    pointerEvents: "none",
                                                    animation: `sparkOrbit 2.4s linear infinite`,
                                                    "@keyframes sparkOrbit": {
                                                        to: { offsetDistance: "100%" },
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Fade>
                ))}
            </Box>

            {/* ── Full-screen document reader dialog (mobile only) ── */}
            <Dialog
                open={readOpen !== null}
                onClose={() => setReadOpen(null)}
                fullScreen
                slots={{ transition: SlideUp }}
                sx={{ zIndex: 1400 }}
                slotProps={{
                    paper: {
                        sx: {
                            background: "#ffffff",
                        },
                    },
                }}
            >
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        bgcolor: "#fff",
                        color: TEXT_PRIMARY,
                        borderBottom: "1px solid rgba(0,40,128,0.06)",
                    }}
                >
                    <Toolbar>
                        <IconButton edge="start" onClick={() => setReadOpen(null)} sx={{ color: "rgba(0,40,128,0.7)" }}>
                            <CloseIcon />
                        </IconButton>
                        <Typography
                            sx={{
                                ml: 1,
                                fontWeight: 700,
                                fontSize: "1rem",
                                flex: 1,
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {readCampaign?.title || ""}
                        </Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent sx={{ px: 2.5, py: 3 }}>
                    {readCampaign?.content && (
                        <Typography
                            component="div"
                            sx={{
                                fontSize: "0.92rem",
                                color: TEXT_SECONDARY,
                                fontFamily: "'Inter',sans-serif",
                                lineHeight: 1.75,
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap",
                            }}
                            dangerouslySetInnerHTML={{ __html: readCampaign.content }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Confirmation dialog ── */}
            <Dialog
                open={confirmOpen !== null}
                onClose={handleConfirmClose}
                fullScreen={mobile}
                slots={mobile ? { transition: SlideUp } : undefined}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: mobile ? 0 : "20px",
                            background: "#ffffff",
                            maxWidth: mobile ? "100%" : 460,
                            width: "100%",
                            p: mobile ? 0 : 0.5,
                            boxShadow: mobile ? "none" : "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)",
                        },
                    },
                }}
                sx={{ zIndex: 1400 }}
            >
                {mobile && (
                    <AppBar
                        position="sticky"
                        elevation={0}
                        sx={{
                            bgcolor: "#fff",
                            color: TEXT_PRIMARY,
                            borderBottom: "1px solid rgba(0,40,128,0.06)",
                        }}
                    >
                        <Toolbar>
                            <IconButton edge="start" onClick={handleConfirmClose} sx={{ color: "rgba(0,40,128,0.7)" }}>
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 1, fontWeight: 700, fontSize: "1rem", flex: 1 }}>
                                <Langs str="VSEC_CONFIRM_APPROVAL_TITLE" />
                            </Typography>
                        </Toolbar>
                    </AppBar>
                )}

                {!mobile && (
                    <DialogTitle
                        sx={{
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                            fontWeight: 650,
                            fontSize: "1.05rem",
                            color: TEXT_PRIMARY,
                            letterSpacing: "-0.02em",
                            pt: 3,
                            pb: 1,
                            px: 3,
                        }}
                    >
                        <Langs str="VSEC_CONFIRM_APPROVAL_TITLE" />
                    </DialogTitle>
                )}

                <DialogContent sx={{ px: 3, pb: 1, pt: mobile ? 3 : undefined }}>
                    {/* Main confirmation question */}
                    <DialogContentText
                        sx={{
                            fontFamily: "'Inter',sans-serif",
                            fontSize: "0.9rem",
                            color: TEXT_SECONDARY,
                            mb: 2,
                        }}
                    >
                        <Langs str="VSEC_CONFIRM_APPROVAL_MSG" />
                    </DialogContentText>

                    {/* Irreversibility warning inside the dialog */}
                    <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={1}
                        sx={{
                            px: 1.5,
                            py: 1.2,
                            borderRadius: "10px",
                            background: "rgba(255,144,21,0.05)",
                            border: "1px solid rgba(255,144,21,0.16)",
                        }}
                    >
                        <WarningAmberRoundedIcon
                            sx={{ color: ORANGE, fontSize: 17, mt: 0.15, flexShrink: 0 }}
                        />
                        <Typography
                            sx={{
                                fontSize: "0.78rem",
                                color: TEXT_MUTED,
                                fontFamily: "'Inter',sans-serif",
                                fontWeight: 500,
                                lineHeight: 1.5,
                            }}
                        >
                            <Langs str="VSEC_CONFIRM_APPROVAL_WARNING" />
                        </Typography>
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 2.5,
                        pt: 2,
                        gap: 1,
                        ...(mobile && {
                            position: "sticky",
                            bottom: 0,
                            bgcolor: "#fff",
                            borderTop: "1px solid rgba(0,40,128,0.06)",
                            flexDirection: "column",
                            p: 2,
                        }),
                    }}
                >
                    {/* Cancel button */}
                    <Button
                        onClick={handleConfirmClose}
                        fullWidth={mobile}
                        sx={{
                            borderRadius: "10px",
                            px: 2.5,
                            py: 0.9,
                            minHeight: 48,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.88rem",
                            color: TEXT_MUTED,
                            fontFamily: "'Inter',sans-serif",
                            border: `1px solid ${BORDER_SUBTLE}`,
                            background: "transparent",
                            transition: `all 250ms ${EASE}`,
                            "&:hover": {
                                background: "rgba(0,0,0,0.04)",
                                borderColor: "rgba(0,0,0,0.12)",
                            },
                        }}
                    >
                        <Langs str="VSEC_BTN_CANCEL" />
                    </Button>

                    {/* Confirm / approve button */}
                    <Button
                        onClick={handleConfirmAcknowledge}
                        variant="contained"
                        fullWidth={mobile}
                        startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "17px !important" }} />}
                        sx={{
                            borderRadius: "10px",
                            px: 2.5,
                            py: 0.9,
                            minHeight: 48,
                            textTransform: "none",
                            fontWeight: 650,
                            fontSize: "0.88rem",
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                            background: ORANGE_GRADIENT,
                            color: "#fff",
                            boxShadow: `0 3px 12px rgba(255,144,21,0.25)`,
                            transition: `all 250ms ${EASE}`,
                            "&:hover": {
                                background: ORANGE_GRADIENT,
                                boxShadow: `0 5px 18px rgba(255,144,21,0.35)`,
                                transform: "translateY(-1px)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Langs str="VSEC_BTN_CONFIRM" />
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
